import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { lazy, Suspense, useRef } from 'react';
import { Building2 } from '@/components/icons/glyphs/Building2'; import { Minimize2 } from '@/components/icons/glyphs/Minimize2'; import { Search } from '@/components/icons/glyphs/Search';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipProvider } from '@/components/ui/tooltip';
import { setPresentation, usePresentation } from '@/features/demos/presentation';
import { useSession } from '@/hooks/use-session';
import { useCurrentTenant } from '@/hooks/use-tenant';
import { cn } from '@/lib/cn';
import { Logo } from './logo';
import { isNavActive, type NavItem, PLATFORM_NAV } from './nav-config';
import { TenantSwitcher } from './tenant-switcher';
import { ThemeToggle } from './theme-toggle';
import { useTenantNavItems } from './use-nav-items';
import { usePaletteShortcut } from './use-palette-shortcut';
import { UserMenu } from './user-menu';

const CommandPalette = lazy(() => import('./command-palette').then((m) => ({ default: m.CommandPalette })));

const linkCls =
  'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground justify-center lg:justify-start';
const linkActiveCls =
  'bg-sidebar-accent font-bold text-sidebar-accent-foreground before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-full before:bg-brand-strong';
const headingCls =
  'hidden px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-sidebar-foreground/60 lg:block';

function NavLink({
  item,
  pathname,
  tenantId,
  ariaPrefix,
}: {
  item: NavItem;
  pathname: string;
  tenantId: string | null;
  ariaPrefix?: string;
}) {
  const active = isNavActive(item, pathname);
  return (
    <Link
      to={item.to}
      {...(item.tenantScoped && tenantId ? { search: { tenant: tenantId } } : {})}
      activeOptions={{ exact: true }}
      preload="intent"
      aria-label={ariaPrefix ? `${ariaPrefix}: ${item.label}` : item.label}
      aria-current={active ? 'page' : undefined}
      title={item.label}
      className={cn(linkCls, active && linkActiveCls)}
    >
      <item.icon className="size-[18px] shrink-0" />
      <span className="hidden lg:inline">{item.label}</span>
    </Link>
  );
}

/** Platform admin sin tenant elegido (o sin tenants): el grupo TENANT no tiene a qué apuntar. */
function NoTenantState() {
  return (
    <Link
      to="/platform/tenants"
      aria-label="Elige un tenant"
      title="Elige un tenant"
      className={cn(
        linkCls,
        'border border-dashed border-sidebar-border text-sidebar-foreground/80 lg:justify-center lg:text-center',
      )}
    >
      <Building2 className="size-[18px] shrink-0 lg:hidden" aria-hidden />
      <span className="hidden lg:inline">Elige un tenant</span>
    </Link>
  );
}

function Sidebar() {
  const { tenant, tenantId, isLoading } = useCurrentTenant();
  const { isPlatformAdmin } = useSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const items = useTenantNavItems(isPlatformAdmin, tenantId);
  const showEmpty = isPlatformAdmin && !isLoading && !tenant;
  return (
    <aside
      className="flex w-16 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:w-60"
      aria-label="Navegación principal"
    >
      <div className="flex h-14 items-center gap-2.5 px-4">
        <Logo className="size-7 shrink-0" />
        <span className="hidden text-base font-bold tracking-tight text-foreground lg:inline">CoreAI</span>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3">
        {isPlatformAdmin ? (
          <section aria-labelledby="nav-platform" className="space-y-1">
            <p id="nav-platform" className={headingCls}>
              Plataforma
            </p>
            {PLATFORM_NAV.map((item) => (
              <NavLink key={item.to} item={item} pathname={pathname} tenantId={tenantId} ariaPrefix="Plataforma" />
            ))}
          </section>
        ) : null}
        <section
          aria-labelledby="nav-tenant"
          className={cn('space-y-1', isPlatformAdmin && 'mt-4 border-t border-sidebar-border pt-3')}
        >
          <p
            id="nav-tenant"
            className="hidden gap-1 px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-sidebar-foreground/60 lg:flex"
            title={tenant?.name}
          >
            <span className="shrink-0">Tenant</span>
            {tenant ? (
              <>
                {' '}
                <span>·</span> <span className="min-w-0 truncate normal-case tracking-normal">{tenant.name}</span>
              </>
            ) : null}
          </p>
          {isLoading ? (
            <Skeleton className="h-9 w-full" />
          ) : showEmpty ? (
            <NoTenantState />
          ) : (
            items.map((item) => <NavLink key={item.to} item={item} pathname={pathname} tenantId={tenantId} />)
          )}
        </section>
      </nav>
      <div className="hidden px-4 py-4 text-xs text-sidebar-foreground/50 lg:block">
        Demo local · <kbd className="rounded bg-muted px-1 py-0.5 font-sans">⌘K</kbd> para buscar
      </div>
    </aside>
  );
}

function Topbar({ onOpenPalette }: { onOpenPalette: () => void }) {
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card px-4">
      <TenantSwitcher />
      <button
        type="button"
        onClick={onOpenPalette}
        className="hidden h-9 min-w-56 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm text-muted-foreground transition-colors hover:border-muted-foreground/40 md:flex"
      >
        <Search className="size-4" aria-hidden />
        <span className="flex-1 text-left">Buscar o crear…</span>
        <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[11px]">⌘K</kbd>
      </button>
      <div className="flex-1" />
      <ThemeToggle />
      <UserMenu />
    </header>
  );
}

export function AppShell() {
  const presenting = usePresentation();
  const [open, setOpen] = usePaletteShortcut();
  // La paleta (cmdk) se descarga la primera vez que se abre y queda montada después.
  const paletteSeen = useRef(false);
  if (open) paletteSeen.current = true;

  return (
    <TooltipProvider>
      <div className="relative flex h-dvh overflow-hidden bg-background">
        {presenting ? null : <Sidebar />}
        <div className="flex min-w-0 flex-1 flex-col">
          {presenting ? null : <Topbar onOpenPalette={() => setOpen(true)} />}
          <main id="main" className={cn('relative min-h-0 flex-1 overflow-y-auto', presenting ? 'p-4' : 'p-6 lg:p-8')}>
            <div className="animate-page-in mx-auto h-full w-full max-w-[1400px]">
              <Outlet />
            </div>
          </main>
        </div>
        {presenting ? (
          <Button
            variant="secondary"
            size="sm"
            className="fixed bottom-4 right-4 z-40 opacity-60 shadow-md hover:opacity-100"
            onClick={() => setPresentation(false)}
          >
            <Minimize2 className="size-3.5" aria-hidden />
            Salir del modo presentación (P)
          </Button>
        ) : null}
        {paletteSeen.current ? (
          <Suspense fallback={null}>
            <CommandPalette open={open} onOpenChange={setOpen} />
          </Suspense>
        ) : null}
      </div>
    </TooltipProvider>
  );
}
